import React, { useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import Page from "../../page-layout";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { BackButton } from "../../../parhato2/src/entities";
import { useSharedCartQuery } from "../../../providers/cart";
import { useGetIncludeProductsQuery } from "../../../providers/products";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { useCartData } from "../../../providers/cartDataStore";
import { useConfig } from "../../../providers/config";
import { LineItem, Product, SharedCartProductRef } from "@core/types";
import { Image } from "../../../components/Image/Image";
import { IonIcon, IonSkeletonText } from "@ionic/react";
import { shareOutline } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { prettifyNumbers } from "../../../utils/prettifyNumbers";
import useOpenProductDetailSheet from "../../../hooks/useOpenProductDetailSheet";
import { copyToClipboard } from "../../../utils/copyToClipboard";
import { shareContent } from "../../../utils/shareUtils";
import { toast } from "react-toastify";

const SharedCartPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const config = useConfig();
  const { repeatOrder } = useCartData();
  const { activeShippingZone } = useActiveOrMainShippingZone();
  const openProductDetailSheet = useOpenProductDetailSheet();

  const { data: shared, isLoading: isLoadingShared, isError, error } = useSharedCartQuery(id);

  const refs: SharedCartProductRef[] = useMemo(() => {
    if (!shared?.products?.length) return [];
    return shared.products;
  }, [shared]);

  const includeIds = useMemo(() => refs.map((r) => String(r.id)), [refs]);

  const { data: products, isLoading: isLoadingProducts } = useGetIncludeProductsQuery({
    include: includeIds,
    params: { zoneId: activeShippingZone.id },
    options: {
      enabled: !!refs.length && !!activeShippingZone.id,
    },
  });

  const productById = useMemo(() => {
    const map = new Map<string, Product>();
    (products ?? []).forEach((p) => map.set(String(p.id), p));
    return map;
  }, [products]);

  const resolvedCount = useMemo(() => refs.filter((r) => productById.has(String(r.id))).length, [refs, productById]);

  const handleAddAll = () => {
    if (!products?.length || !refs.length) {
      return;
    }

    const resolvedProducts: Product[] = [];
    const lineItems: LineItem[] = [];

    refs.forEach((ref, index) => {
      const p = productById.get(String(ref.id));
      if (!p) return;
      resolvedProducts.push(p);
      lineItems.push({
        id: `shared-${ref.id}-${index}`,
        productId: p.id,
        quantity: ref.quantity,
      });
    });

    if (!resolvedProducts.length) {
      return;
    }

    repeatOrder(resolvedProducts, lineItems);
    history.push("/cart");
  };

  const handleOpenProduct = (productId: string) => {
    openProductDetailSheet({ id: productId });
  };

  const handleShare = async () => {
    const sharedCartUrl = `${config.appUrl}/shared-cart/${id}`;
    await copyToClipboard(sharedCartUrl);
    toast.success("Ссылка скопирована в буфер обмена");
    shareContent({
      title: shared?.name ?? "Поделиться корзиной",
      text: `Поделиться корзиной: ${sharedCartUrl}`,
      url: sharedCartUrl,
    });
  };

  const showSkeleton = isLoadingShared || (refs.length > 0 && isLoadingProducts);
  const headerTitle = shared?.name ?? t("Shared Cart");
  return (
    <Page>
      <Page.Header className="flex items-center justify-start h-16 mt-[env(safe-area-inset-top)]">
        <MaxWidthContainer classList="flex items-center justify-center bg-white w-full">
          <div className="flex justify-center items-center w-full relative">
            <div className="absolute left-2.5">
              <BackButton />
            </div>
            <div className="flex-1 text-center text-[1.25rem] font-bold">{headerTitle}</div>
            <button type="button" className="absolute right-2.5 p-1" onClick={handleShare}>
              <IonIcon icon={shareOutline} className="text-2xl text-black" />
            </button>
          </div>
        </MaxWidthContainer>
      </Page.Header>

      <Page.Content scrollEvents className="my-4 pb-28">
        <MaxWidthContainer>
          {isError && (
            <div className="rounded-2xl bg-white p-5 shadow text-center text-red-600 text-sm">
              {error instanceof Error ? error.message : "Не удалось загрузить корзину"}
            </div>
          )}

          {showSkeleton && (
            <div className="flex flex-col gap-3 p-2.5">
              <IonSkeletonText animated className="h-8 rounded-lg" />
              <IonSkeletonText animated className="h-20 rounded-xl" />
              <IonSkeletonText animated className="h-24 rounded-xl" />
            </div>
          )}

          {!showSkeleton && shared && (
            <div className="flex flex-col gap-5 p-2.5">
              {!!shared.description?.length && (
                <div className="w-full bg-white shadow-[0_0_20px_rgba(0,0,0,0.12)] rounded-2xl p-7 flex flex-col gap-2">
                  <p className="text-base whitespace-pre-wrap">{shared.description}</p>
                </div>
              )}

              <div className="w-full bg-white shadow-[0_0_20px_rgba(0,0,0,0.12)] rounded-2xl p-5 flex flex-col gap-3">
                <div className="text-sm font-semibold text-gray-700">Товары</div>
                {refs.map((ref) => {
                  const productFromSharedCart = productById.get(String(ref.id));

                  if (!productFromSharedCart) {
                    return (
                      <div
                        key={`missing-${ref.id}`}
                        className="flex items-center justify-between rounded-xl border border-dashed border-gray-200 px-2 py-3"
                      >
                        <span className="text-sm text-gray-500">Товар #{ref.id} недоступен в вашей зоне</span>
                        <span className="text-sm font-semibold">×{ref.quantity}</span>
                      </div>
                    );
                  }

                  const unitPrice = parseFloat(productFromSharedCart.price || "0");
                  const image = productFromSharedCart.images?.[0];
                  const lineTotal = unitPrice * ref.quantity;

                  return (
                    <button
                      type="button"
                      key={ref.id}
                      className="text-left w-full"
                      onClick={() => handleOpenProduct(productFromSharedCart.id)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Image
                            lazyLoad
                            image={image}
                            className="min-w-16 min-h-16 max-w-16 max-h-16 rounded-lg shrink-0"
                          />
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-medium truncate">{productFromSharedCart.name}</span>
                            <span className="text-gray-500 text-xs">
                              {ref.quantity}{" "}
                              {t(productFromSharedCart.portionUnit ? productFromSharedCart.portionUnit : "PCE")} ×{" "}
                              {prettifyNumbers(unitPrice, { showCurrencySymbol: true })}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs font-semibold shrink-0">
                          {prettifyNumbers(lineTotal, { showCurrencySymbol: true })}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {!showSkeleton && shared && refs.length > 0 && (
                <div className="px-3 mb-[env(safe-area-inset-bottom)] pb-24 pt-2 bg-gradient-to-t from-white via-white to-transparent">
                  <MaxWidthContainer classList="flex justify-center">
                    <button
                      type="button"
                      onClick={handleAddAll}
                      disabled={resolvedCount === 0}
                      className="w-full max-w-md rounded-xl bg-primary font-semibold py-3.5 disabled:opacity-50"
                    >
                      Добавить в корзину
                    </button>
                  </MaxWidthContainer>
                </div>
              )}
            </div>
          )}
        </MaxWidthContainer>
      </Page.Content>
    </Page>
  );
};

export default SharedCartPage;
